import React, { useState, useCallback } from "react";
import {
  useTranslate,
  useApiUrl,
  useNavigation,
  useInvalidate,
  IResourceComponentsProps,
} from "@pankod/refine-core";
import {
  List,
  Table,
  Tag,
  Button,
  Space,
  useTable,
  DateField,
  ShowButton,
} from "@pankod/refine-antd";
import { Progress, Modal, message } from "antd";
import { CheckCircleOutlined, PlayCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import { IVerificationJob, VerificationJobStatus } from "interfaces";
import TriggerModal from "./triggerModal";

const STATUS_COLORS: Record<VerificationJobStatus, string> = {
  pending: "default",
  processing: "processing",
  completed: "success",
  partial: "warning",
  failed: "error",
};

const SCOPE_LABELS: Record<string, string> = {
  all: "verification.jobs.scope_all",
  unverified: "verification.jobs.scope_unverified",
  retry: "verification.jobs.scope_retry",
};

export const VerificationJobList: React.FC<IResourceComponentsProps> = () => {
  const t = useTranslate();
  const apiUrl = useApiUrl();
  const APIToken = localStorage.getItem("api-token");
  const { show } = useNavigation();
  const invalidate = useInvalidate();

  const [modalVisible, setModalVisible] = useState(false);

  const { tableProps, tableQueryResult } = useTable<IVerificationJob>({
    resource: "verification-jobs",
    initialSorter: [{ field: "created_at", order: "desc" }],
    queryOptions: {
      refetchInterval: (data: any) => {
        const jobs: IVerificationJob[] = data?.data || [];
        const hasActive = jobs.some(
          (j) => j.status === "pending" || j.status === "processing"
        );
        return hasActive ? 5000 : false;
      },
    },
  });

  const jobs: IVerificationJob[] = tableQueryResult?.data?.data || [];

  const handleApply = useCallback(
    async (job: IVerificationJob) => {
      Modal.confirm({
        title: t("verification.apply.confirm_title"),
        content: t("verification.apply.confirm_body", {
          total: job.disagreement_count,
          human: "—",
          ai: "—",
        }),
        onOk: async () => {
          try {
            const res = await axios.post(
              `${apiUrl}/verification-jobs/${job.id}/apply`,
              {},
              { headers: { Authorization: `Bearer ${APIToken}` } }
            );
            message.success(
              t("verification.apply.success", {
                parents: res.data?.parents_updated ?? 0,
                children: res.data?.children_updated ?? 0,
              })
            );
            invalidate({
              resource: "verification-jobs",
              invalidates: ["list"],
            });
          } catch (error) {
            const msg =
              axios.isAxiosError(error) && error.response?.data?.message
                ? error.response.data.message
                : "Failed to apply results";
            message.error(msg);
          }
        },
      });
    },
    [apiUrl, APIToken, t, invalidate]
  );

  const handleTriggerSuccess = () => {
    message.success(t("verification.trigger.success"));
    invalidate({
      resource: "verification-jobs",
      invalidates: ["list"],
    });
  };

  return (
    <List
      headerProps={{
        extra: [
          <Button
            key="trigger"
            type="primary"
            icon={<PlayCircleOutlined />}
            onClick={() => setModalVisible(true)}
          >
            {t("verification.jobs.run_new")}
          </Button>,
        ],
      }}
      title={t("verification.jobs.title")}
    >
      <Table
        {...tableProps}
        rowKey="id"
        scroll={{ x: 1200 }}
      >
        <Table.Column
          dataIndex="created_at"
          title={t("verification.jobs.started")}
          render={(value) => <DateField value={value} format="LLL" />}
          sorter
        />
        <Table.Column
          dataIndex="triggered_by"
          title={t("verification.jobs.triggered_by")}
        />
        <Table.Column
          dataIndex="scope"
          title={t("verification.jobs.scope")}
          render={(value: string, record: IVerificationJob) => (
            <Space>
              <Tag>{t(SCOPE_LABELS[value] || value)}</Tag>
              {value === "retry" && record.parent_job_id && (
                <Button
                  type="link"
                  size="small"
                  onClick={() =>
                    show("verification-jobs", record.parent_job_id!)
                  }
                >
                  {t("verification.jobs.retried_from")}
                </Button>
              )}
            </Space>
          )}
        />
        <Table.Column
          dataIndex="model_name"
          title={t("verification.jobs.model")}
          render={(value: string, record: IVerificationJob) => (
            <Space>
              <span>{value}</span>
              <Tag>{record.model_provider}</Tag>
            </Space>
          )}
        />
        <Table.Column
          dataIndex="status"
          title={t("verification.jobs.status")}
          render={(value: VerificationJobStatus) => (
            <Tag color={STATUS_COLORS[value]}>
              {t(`verification.jobs.status_${value}`)}
            </Tag>
          )}
        />
        <Table.Column
          title={t("verification.jobs.progress")}
          render={(_, record: IVerificationJob) => {
            const percent =
              record.total_items > 0
                ? Math.round(
                    (record.processed_items / record.total_items) * 100
                  )
                : 0;
            return (
              <Space>
                <Progress
                  percent={percent}
                  size="small"
                  style={{ width: 100 }}
                  status={record.status === "failed" ? "exception" : undefined}
                />
                <span>
                  {record.processed_items}/{record.total_items}
                </span>
              </Space>
            );
          }}
        />
        <Table.Column
          title={t("verification.jobs.disagreements")}
          render={(_, record: IVerificationJob) =>
            record.status === "completed" || record.status === "partial"
              ? `${record.disagreement_count} (${record.disagreement_percent?.toFixed(1) ?? 0}%)`
              : "—"
          }
        />
        <Table.Column
          dataIndex="applied_at"
          title={t("verification.jobs.applied")}
          render={(value: string | null) =>
            value ? (
              <Space>
                <CheckCircleOutlined style={{ color: "#52c41a" }} />
                <DateField value={value} format="LLL" />
              </Space>
            ) : null
          }
        />
        <Table.Column
          title={t("table.actions")}
          render={(_, record: IVerificationJob) => (
            <Space>
              <ShowButton
                hideText
                size="small"
                resourceNameOrRouteName="verification-jobs"
                recordItemId={record.id}
              />
              {(record.status === "completed" ||
                record.status === "partial") &&
                !record.applied_at && (
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => handleApply(record)}
                  >
                    {t("verification.jobs.apply_results")}
                  </Button>
                )}
              {record.retried_by_job_id && (
                <Button
                  type="link"
                  size="small"
                  onClick={() =>
                    show("verification-jobs", record.retried_by_job_id!)
                  }
                >
                  {t("verification.jobs.retried_by")}
                </Button>
              )}
            </Space>
          )}
        />
      </Table>

      <TriggerModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        onSuccess={handleTriggerSuccess}
        jobs={jobs}
      />
    </List>
  );
};
